import { D as DeviceType } from './ui.type-12100305.js';
import { L as LogUtility } from './log-a824be80.js';

/**
 * find the parent dom with selector
 * if selector is a component tag name like 'vega-card',
 * we can call by findParent<HTMLVegaCardElement>(this.el, "vega-card") get the HTMLVegaCardElement;
 *
 * The function is generic, meaning that it can be used with any type of element
 *
 * @typedef HTMLElementTagNameMap
 * @typedef T
 * @param {HTMLElement} el - The element to start searching from.
 * @param {keyof HTMLElementTagNameMap | string} selector - The selector to find the parent element with.
 * @returns {T | null} The first element that matches the selector.
 */
function findParent(el, selector) {
  if (!el)
    return null;
  const parent = el.parentElement;
  return (parent === null || parent === void 0 ? void 0 : parent.matches(selector)) ? parent : findParent(parent, selector);
}
/**
 * It returns the device type based on the user agent
 *
 * @returns {DeviceType} The device type.
 */
const getDeviceType = () => {
  const userAgent = navigator.userAgent;
  if (/(tablet|ipad|playbook|silk|Macintosh)|(android(?!.*mobi))/i.test(userAgent) &&
    navigator.maxTouchPoints > 0) {
    return DeviceType.TABLET;
  }
  else if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(userAgent)) {
    return DeviceType.MOBILE;
  }
  return DeviceType.DESKTOP;
};
/**
 * return class sting without '', null, undefined, Nan
 *
 * @param {string[]} classes - class array
 * @returns {string} - class string
 */
const arrayToClassString = (classes) => {
  if (classes) {
    return classes.filter((c) => c).join(' ');
  }
  else {
    return '';
  }
};
/**
 * If the element is the target, return true, otherwise, if the element has a parent, return the result
 * of calling isParent on the parent, otherwise return false.
 *
 * @param {HTMLElement} element - The element to check if it's a parent of the target.
 * @param {HTMLElement} target - The element that you want to check if it's a parent of the element.
 * @returns {boolean} A boolean value.
 */
function isParent(element, target) {
  if (element === target)
    return true;
  if (element) {
    return isParent(element.parentElement, target);
  }
  else {
    return false;
  }
}
/**
 * "Scroll the container to the item."
 * The function takes two parameters:
 * container: The container to scroll.
 * item: The item to scroll to.
 * The function scrolls the container to the item
 *
 * @param {HTMLElement} container - The container element that you want to scroll.
 * @param {HTMLElement} item - The item to scroll to.
 * @returns {void} - return.
 */
function containerScrollToItem(container, item) {
  container.scrollTop = item.offsetTop;
}
/**
 * Check if the element is visible by checking if the element's height is greater than 0.
 *
 * @param {HTMLElement} element - HTMLElement - the element to check if it's visible
 * @returns {boolean} A boolean value.
 */
function isElementVisible(element) {
  return element.getBoundingClientRect().height > 0;
}
/**
 * It takes a string and returns a number, or null if the string is empty
 *
 * @param {string} input - The string to extract the number from.
 * @returns {number | null} A function that takes a string and returns a number or null.
 */
function extractNumberFromString(input) {
  if (input) {
    const result = input.replace(/\D/g, '');
    return result === '' ? null : Number(result);
  }
  return null;
}
/**
 * It takes a hexadecimal color string and returns an object with the red, green, and blue values
 *
 * @param {string} hex - The hexadecimal color value to convert to RGB.
 * @returns {RGBType | null} - The RGB color value
 */
function hexToRGB(hex) {
  hex = getHexFromShorthand(hex);
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16),
    }
    : null;
}
/**
 * It converts a hexadecimal color code from shorthand form to full form
 *
 * @param {string} hex - The hexadecimal color code that you want to convert to full form.
 * @returns {string} the hexFullForm variable.
 */
function getHexFromShorthand(hex) {
  if (!hex)
    hex = '';
  // Expand shorthand form (e.g. "03F") to full form (e.g. "#0033FF")
  const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
  const hexFullForm = hex.replace(shorthandRegex, function (_m, r, g, b) {
    return '#' + r + r + g + g + b + b;
  });
  return hexFullForm;
}
/**
 * It returns true if the text in the element is wrapped.
 *
 * @param {HTMLElement} element - The element to be judged.
 * @returns {boolean} A boolean value.
 */
function isTextWrapped(element) {
  const lineHeight = parseFloat(getElementComputedStyle(element, 'line-height'));
  if (!isNaN(lineHeight)) {
    return element.getBoundingClientRect().height > lineHeight;
  }
  else {
    LogUtility.warn('The line height of this element is not obtained');
    return false;
  }
}
/**
 * "Get the computed style of an element."
 * The first parameter is the element whose style we want to get. The second parameter is the name of
 * the style we want to get
 *
 * @param {HTMLElement} element - The element whose style you want to get.
 * @param {string} styleName - The name of the style you want to get.
 * @returns {string} The computed style of the element.
 */
function getElementComputedStyle(element, styleName) {
  return window.getComputedStyle(element).getPropertyValue(styleName);
}

export { arrayToClassString as a, isElementVisible as b, isTextWrapped as c, getElementComputedStyle as d, extractNumberFromString as e, findParent as f, getDeviceType as g, hexToRGB as h, isParent as i, getHexFromShorthand as j, containerScrollToItem as k };
